import type { StoreApi, UseBoundStore } from 'zustand';

type TEntity<T> = T & { id?: string };
type TStore<S> = UseBoundStore<StoreApi<S>>;
type TState<T> = {
  assigned: TEntity<T>[];
  setAssigned: (entities: TEntity<T>[]) => void;
};

export const useAssignHandlers = <T, S>(
  entities: TEntity<T>[],
  store: TStore<S>,
) => {
  const setAssigned = (store.getState() as TState<T>).setAssigned;

  const isAssigned = (record: TEntity<T>) => {
    const state = store.getState() as TState<T>;
    if (!record.id) return false;
    const list = state.assigned as TEntity<T>[];
    return list.some((entity) => entity.id === record.id);
  };

  const isAssignedAll = () => {
    const state = store.getState() as TState<T>;

    return (
      entities.length > 0 &&
      entities.every((entity) =>
        (state.assigned as TEntity<T>[]).some((a) => a.id === entity.id),
      )
    );
  };

  const onAssignAll = () => {
    if (isAssignedAll()) {
      setAssigned([]);
    } else {
      setAssigned(entities);
    }
  };

  const onAssign = (entity: TEntity<T>) => {
    if (!entity.id) return;
    const state = store.getState() as TState<T>;

    const assigned = state.assigned as TEntity<T>[];
    const exists = assigned.some((e) => e.id === entity.id);

    if (exists) {
      setAssigned(assigned.filter((e) => e.id !== entity.id));
    } else {
      setAssigned([...assigned, entity]);
    }
  };

  return { onAssign, onAssignAll, isAssigned, isAssignedAll };
};
